import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, CopyIcon, Share2Icon, XIcon } from 'lucide-react';
import { buildShareText, getTier } from '../utils/score';
import type { StatusMap } from '../types/show';
interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  statuses: StatusMap;
  seenCount: number;
  wantCount: number;
  total: number;
}
export function ShareModal({
  open,
  onClose,
  statuses,
  seenCount,
  wantCount,
  total
}: ShareModalProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const tier = getTier(seenCount);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  useEffect(() => {
    if (!open) return;
    setCopyState('idle');
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const text = buildShareText(statuses, total, seenCount);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };
  const handleNativeShare = async () => {
    try {
      await navigator.share({
        text
      });
    } catch {
      // User dismissed the share sheet.
    }
  };
  return <AnimatePresence data-id="element-49" data-mp="21xcd92uapvjc">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/50 p-4 sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} onClick={onClose} data-id="element-50" data-mp="220go2j29npiz">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="share-title" className="w-full max-w-sm rounded-xl bg-paper p-6 shadow-xl" initial={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-51" data-mp="3nbq9iyldr6os">
            <div className="flex items-start justify-between" data-id="element-52" data-mp="q4tl9dbzncgy">
              <p className="font-serif text-base italic text-muted" data-id="element-53" data-mp="q6o50obiwyjg">The Checklist</p>
              <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-2 grid h-9 w-9 place-items-center rounded-full text-muted hover:bg-rule hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink" data-id="element-54" data-mp="1mtsv2ws80mb9">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-55" data-mp="191v1juazsz07" />
              </button>
            </div>

            <h2 id="share-title" className="mt-1 font-serif text-2xl leading-tight text-ink" data-id="element-56" data-mp="26kwizui89zjh">
              I've seen <span className="tabular-nums" data-id="element-57" data-mp="293hpo4t57syh">{seenCount}</span> of the 100 best TV shows
              of the century
            </h2>
            <p className="mt-2 font-sans text-sm font-bold text-ink" data-id="element-58" data-mp="2e1t12jz36bit">{tier.title}</p>

            <div className="mt-5 grid grid-cols-10 gap-1" role="img" aria-label={`${seenCount} seen, ${wantCount} on the watchlist, out of ${total}`} data-id="element-59" data-mp="2j15uxoodkiuw">
              {Array.from({
            length: total
          }, (_, i) => {
            const s = statuses[i + 1];
            return <span key={i} className={`aspect-square rounded-[3px] ${s === 'seen' ? 'bg-ink' : s === 'want' ? 'bg-want' : 'bg-rule'}`} data-id="element-60" data-mp="2wzvt77mxkoku" />;
          })}
            </div>

            <div className="mt-3 flex gap-4 font-sans text-xs text-muted" data-id="element-61" data-mp="3au68kyd1f6e8">
              <span className="flex items-center gap-1.5" data-id="element-62" data-mp="3fr6jbmku0xbo">
                <span className="h-2.5 w-2.5 rounded-[2px] bg-ink" data-id="element-63" data-mp="3fpllno380y9l" /> Seen {seenCount}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-64" data-mp="2y3m6p6jxk0vm">
                <span className="h-2.5 w-2.5 rounded-[2px] bg-want" data-id="element-65" data-mp="2y34csgkqpyqv" /> Want to see {wantCount}
              </span>
            </div>

            <div className="mt-6 flex flex-col gap-2" data-id="element-66" data-mp="3bwvb0st0tw7a">
              <button type="button" onClick={handleCopy} className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-ink font-sans text-sm font-semibold text-paper transition-transform duration-150 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2" data-id="element-67" data-mp="36x8yymoxim02">
                {copyState === 'copied' ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-68" data-mp="8e21cr3d5rie" /> : <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-69" data-mp="3f7m1z4lul31" />}
                {copyState === 'copied' ? 'Copied — paste it anywhere' : 'Copy my results'}
              </button>
              {canNativeShare && <button type="button" onClick={handleNativeShare} className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-ink font-sans text-sm font-semibold text-ink transition-transform duration-150 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2" data-id="element-70" data-mp="1qd00x3epw1c2">
                  <Share2Icon className="h-4 w-4" aria-hidden="true" data-id="element-71" data-mp="291n7xrggf0sa" />
                  Share
                </button>}
              {copyState === 'error' && <p className="text-center font-sans text-xs text-danger" role="alert" data-id="element-72" data-mp="208tkv7urmx3v">
                  Couldn't access your clipboard. Try the share button or a screenshot.
                </p>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}